import { html, nothing, type TemplateResult } from "lit";
import type { GlobalSearchGroup, GlobalSearchResult } from "../controllers/global-search.ts";
import {
  formatGlobalSearchGroupTime,
  groupSearchResultsBySession,
} from "../controllers/global-search.ts";
import { icons } from "../icons.ts";

/**
 * UX-R7 / JAISIU-3297 — Cmd/Ctrl+K global search modal.
 *
 * Server-side full-transcript search across every chat the operator
 * has access to. Results are grouped by chat (session key) and the
 * modal surfaces each match as "chat title + matched excerpt + Open".
 * The operator can click "Open" to jump into that session.
 *
 * The modal does NOT replace the existing sidebar `<input type="search">`
 * — that filter remains a fast client-side title scan. The global
 * search is the deeper, server-driven escape hatch for needles that
 * live in unloaded chats.
 */

export type GlobalSearchModalProps = {
  /** Show/hide. When false the template returns `nothing`. */
  open: boolean;
  /** The text in the input (controlled). Updates on every keystroke. */
  query: string;
  /** Raw result rows from the gateway. */
  results: readonly GlobalSearchResult[];
  /** True while the debounced RPC is in flight. */
  loading: boolean;
  /** Gateway error or validation error. Shown in place of the list. */
  error: string | null;
  /** True when the query is too short (<2 chars) — gateway will reject. */
  tooShort: boolean;
  /** Called when the operator types in the input. */
  onQueryChange: (next: string) => void;
  /** Called when the operator clicks a result's "Open" button. */
  onOpenResult: (result: GlobalSearchResult) => void;
  /** Called when the operator closes the modal (overlay click, ESC, X). */
  onClose: () => void;
};

const MIN_QUERY_LENGTH = 2;

export function isGlobalSearchQueryTooShort(query: string): boolean {
  return query.trim().length < MIN_QUERY_LENGTH;
}

export function renderGlobalSearchModal(
  props: GlobalSearchModalProps,
): TemplateResult | typeof nothing {
  if (!props.open) {
    return nothing;
  }
  const groups = groupSearchResultsBySession(props.results);
  const totalHits = props.results.length;
  const showEmptyState = !props.loading && !props.error && !props.tooShort && totalHits === 0;
  return html`
    <div
      class="fleet-global-search-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fleet-global-search-title"
      data-testid="fleet-global-search-modal"
      @click=${(e: Event) => {
        if (e.target === e.currentTarget) {
          props.onClose();
        }
      }}
    >
      <div class="fleet-global-search-card" @click=${(e: Event) => e.stopPropagation()}>
        <header class="fleet-global-search__header">
          <label class="fleet-global-search__field">
            <span class="sr-only">Search across all chats</span>
            <span class="fleet-global-search__icon" aria-hidden="true">${icons.search}</span>
            <input
              type="search"
              class="fleet-global-search__input"
              data-testid="fleet-global-search-input"
              placeholder="Search across all chats…"
              .value=${props.query}
              autocomplete="off"
              spellcheck="false"
              @input=${(e: Event) => props.onQueryChange((e.target as HTMLInputElement).value)}
            />
          </label>
          <button
            type="button"
            class="btn icon fleet-global-search__close"
            title="Close (Esc)"
            aria-label="Close global search"
            data-testid="fleet-global-search-close"
            @click=${props.onClose}
          >
            ${icons.x}
          </button>
        </header>
        <div class="fleet-global-search__body" data-testid="fleet-global-search-body">
          ${renderBody(props, groups, showEmptyState)}
        </div>
      </div>
    </div>
  `;
}

function renderBody(
  props: GlobalSearchModalProps,
  groups: GlobalSearchGroup[],
  showEmptyState: boolean,
): TemplateResult {
  if (props.error) {
    return html`<p
      class="fleet-global-search__message fleet-global-search__message--error"
      data-testid="fleet-global-search-error"
    >
      ${props.error}
    </p>`;
  }
  if (props.tooShort) {
    return html`<p
      class="fleet-global-search__message fleet-global-search__message--muted"
      data-testid="fleet-global-search-hint"
    >
      Type at least ${MIN_QUERY_LENGTH} characters to search across all chats.
    </p>`;
  }
  if (props.loading && props.results.length === 0) {
    return html`<p
      class="fleet-global-search__message fleet-global-search__message--muted"
      data-testid="fleet-global-search-loading"
    >
      Searching…
    </p>`;
  }
  if (showEmptyState) {
    return html`<p
      class="fleet-global-search__message fleet-global-search__message--muted"
      data-testid="fleet-global-search-empty"
    >
      No matches across your chats.
    </p>`;
  }
  if (groups.length === 0) {
    return html`<p class="fleet-global-search__message fleet-global-search__message--muted">
      No matches across your chats.
    </p>`;
  }
  return html`
    <div class="fleet-global-search__groups" role="list">
      ${groups.map((group) => renderGroup(group, props))}
    </div>
  `;
}

function renderGroup(group: GlobalSearchGroup, props: GlobalSearchModalProps): TemplateResult {
  const lastTs = group.entries.reduce<number | undefined>((acc, e) => {
    if (e.timestamp === undefined) {
      return acc;
    }
    if (acc === undefined) {
      return e.timestamp;
    }
    return Math.max(acc, e.timestamp);
  }, undefined);
  const timeLabel = formatGlobalSearchGroupTime(lastTs);
  return html`
    <section
      class="fleet-global-search__group"
      role="listitem"
      data-testid="fleet-global-search-group"
      data-session-key=${group.sessionKey}
    >
      <header class="fleet-global-search__group-header">
        <span class="fleet-global-search__group-title">${group.sessionLabel}</span>
        <span class="fleet-global-search__group-meta">
          ${group.entries.length}
          match${group.entries.length === 1 ? "" : "es"}${
            timeLabel ? html` · <time>${timeLabel}</time>` : nothing
          }
        </span>
      </header>
      <ul class="fleet-global-search__entries">
        ${group.entries.map((entry) => renderEntry(entry, props))}
      </ul>
    </section>
  `;
}

function renderEntry(entry: GlobalSearchResult, props: GlobalSearchModalProps): TemplateResult {
  return html`
    <li
      class="fleet-global-search__entry"
      data-testid="fleet-global-search-entry"
      data-session-key=${entry.sessionKey}
      data-message-index=${entry.messageIndex}
    >
      <div class="fleet-global-search__entry-excerpt">
        <span class="fleet-global-search__entry-role">${entry.role}</span>
        <span class="fleet-global-search__entry-text">${entry.matchedText}</span>
      </div>
      <button
        type="button"
        class="btn subtle btn--sm fleet-global-search__open"
        data-testid="fleet-global-search-open"
        title="Open this chat"
        @click=${() => props.onOpenResult(entry)}
      >
        Open
      </button>
    </li>
  `;
}
